<script setup lang="ts">
  import { computed, ref, onMounted } from 'vue'
  import type { ComputedRef } from 'vue'

  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import { vOnClickOutside } from '@vueuse/components'
  import Search from '@/components/Search.vue'

  import { useRoute, useRouter } from 'vue-router/composables'
  import useIcons from '@/composables/useIcons'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useCourse from '@/composables/useCourse'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useI18n from '@/composables/useI18n'

  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'

  import type { CourseBundleOverview, CourseBundleCourseOverview } from '@/services/interfaces/Course'
  import useShopifyClient from '@/composables/useShopifyClient'

  interface NavigationItem {
    to?: string
    icon?: ComputedRef<string>
    label: string
    isExpandable?: boolean
    isCourseBundles?: boolean
    isAcademyAdmin?: boolean
    subItems?: NavigationItem[]
  }

  const route = useRoute()
  const router = useRouter()
  const {
    closeIcon,
    homeIcon,
    allBookmarks,
    settingsIcon,
    arrowRight,
    graduationCapIcon,
    course34iIcon,
    course34dIcon,
    course34fIcon,
    courseAevoIcon,
  } = useIcons()
  const { isMobileDevice, isDesktopDevice } = useBreakpoint()
  const { getPackageBundle, sortedCourseBundlesOverview, courseBundle, onCourseLayout, trialTimeLeft, isTrialExpired } =
    useCourse()
  const {
    extractCourseVertical,
    showReTriggerOnboardingPopup,
    showCompanyOnboardingPopupDetails,
    hasValidUser,
    isLtiUser,
    scrollToTop,
    is34ACompany,
  } = useCommonMixin()
  const { handleWebsiteCheckout } = useShopifyClient()
  const { translateString } = useI18n()

  defineProps<{
    isOpen: boolean
  }>()

  const emit = defineEmits(['close-sidebar'])

  const expandedIndex = ref<number | null>(1)

  const onDashboard = computed(() => route.name === 'Dashboard')

  const navigationItems = computed<NavigationItem[]>(() => {
    const items: NavigationItem[] = [
      {
        to: '/',
        icon: homeIcon,
        label: 'navigation.sidebar.dashboard',
      },
    ]

    if (!is34ACompany.value) {
      items.push({
        icon: graduationCapIcon,
        label: 'navigation.sidebar.examPreparation',
        isExpandable: true,
        isCourseBundles: true,
      })
    }

    if (UserModule.isCompanyEditor && isDesktopDevice.value) {
      items.push({
        label: 'navigation.sidebar.manageCourses',
        to: '/editor',
      })
    }

    if (UserModule.isCompanyAdmin && isDesktopDevice.value) {
      items.push({
        label: 'navigation.sidebar.academyAdmin',
        isExpandable: true,
        isAcademyAdmin: true,
        subItems: [
          {
            to: '/admin/users-and-groups',
            label: 'navigation.admin.usersAndGroups',
          },
          {
            to: '/admin/gamification',
            label: 'navigation.admin.gamification',
          },
        ],
      })
    }

    if (hasValidUser.value) {
      items.push({
        to: '/bookmarks',
        icon: allBookmarks,
        label: 'navigation.sidebar.bookmarks',
      })

      if (!isLtiUser.value) {
        items.push({
          to: '/account',
          icon: settingsIcon,
          label: 'account.accountSettings',
        })
      }

      if (UserModule.companies.length > 1) {
        items.push({
          to: '/company-selection',
          label: 'navigation.mainNav.switchAcademy',
        })
      }
    }

    return items
  })

  const courseVerticalIcon = computed(() => (title: string) => {
    const vertical = extractCourseVertical(title)
    if (vertical === '34i') return course34iIcon.value
    if (vertical === '34d') return course34dIcon.value
    if (vertical === '34f') return course34fIcon.value
    return courseAevoIcon.value
  })

  const isActiveCourseBundle = computed(() => (bundleId: string) => {
    return courseBundle.value?.courseBundleId === bundleId && (onCourseLayout.value || onDashboard.value)
  })

  const hasTrialCourse = computed(() => (bundle: CourseBundleOverview) => {
    return bundle.courses.some((course) => course.trial)
  })

  const getCourseTypePrefix = (courseType: string) => {
    return courseType === 'written' ? 'Schriftlich: ' : 'Mündlich: '
  }

  const getTrialStatus = (course: CourseBundleCourseOverview, typePrefix: string) => {
    const { days } = trialTimeLeft.value(course)

    if (isTrialExpired.value(course)) {
      return {
        text: typePrefix + translateString('testExpired') + '. ->',
        action: translateString('selectCourse'),
        isExpired: true,
        courseType: course.courseType,
      }
    }

    return {
      text:
        typePrefix +
        translateString('testDaysRemaining', {
          days,
          testDayText: days === 1 ? translateString('testDay') : translateString('testDays'),
        }),
      action: null,
      isExpired: false,
      courseType: course.courseType,
    }
  }

  const getProgressStatus = (course: CourseBundleCourseOverview, typePrefix: string, addComma: boolean) => {
    const progress = Math.round((course.numberOfLessonsCompleted / course.numberOfLessons) * 100)

    return {
      text: typePrefix + `${progress}%` + (addComma ? ', ' : ''),
      action: null,
      isExpired: false,
      courseType: course.courseType,
    }
  }

  const getEmptyStatus = (course: CourseBundleCourseOverview) => {
    return {
      text: '',
      action: null,
      isExpired: false,
      courseType: course.courseType,
    }
  }

  const bundleStatus = computed(() => (bundle: CourseBundleOverview) => {
    // Sort courses by type (written first, then oral)
    const sortedCourses = [...bundle.courses].sort((a, b) => {
      if (a.courseType === 'written' && b.courseType === 'oral') return -1
      if (a.courseType === 'oral' && b.courseType === 'written') return 1
      return 0
    })

    const hasAnyTrial = sortedCourses.some((course) => course.trial)

    return sortedCourses.map((course, index) => {
      const typePrefix = getCourseTypePrefix(course.courseType)
      const isLastItem = index === sortedCourses.length - 1
      const addComma = !hasAnyTrial && !isLastItem

      if (course.trial) {
        return getTrialStatus(course, typePrefix)
      }

      if (course.purchased || course.expired || !course.trial) {
        return getProgressStatus(course, typePrefix, addComma)
      }

      return getEmptyStatus(course)
    })
  })

  const toggleExpand = (index: number) => {
    expandedIndex.value = expandedIndex.value === index ? null : index
  }

  const closeSidebar = () => {
    emit('close-sidebar')
  }

  const closeSidebarAndResetExpandedIndex = () => {
    closeSidebar()
    expandedIndex.value = null
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeSidebar()
    }
  }

  const setCourseBundleAndCourse = async (bundleId: string) => {
    if (courseBundle.value?.courseBundleId === bundleId) {
      if (!onCourseLayout.value && !onDashboard.value) {
        router.push({ name: 'Dashboard' })
      }

      closeSidebar()
      return
    }

    if (!onDashboard.value) {
      router.push({ name: 'Dashboard' })
    }

    CourseModule.setCourse(null)
    CourseModule.setCourseBundle(null)

    scrollToTop()

    closeSidebar()

    const bundle = await getPackageBundle(bundleId)

    if (bundle?.courses) {
      const purchasedCourse = bundle.courses.find((course) => course.purchased)
      if (purchasedCourse) {
        CourseModule.setCourse(purchasedCourse)
      } else {
        const writtenCourse = bundle.courses.find((course) => course.courseType === 'written')
        if (writtenCourse) {
          CourseModule.setCourse(writtenCourse)
        }
      }
    }
  }

  const triggerOnboardingPopup = () => {
    closeSidebar()
    showCompanyOnboardingPopupDetails()
  }

  const setActiveExpandableIndex = () => {
    const activeExpandableIndex = navigationItems.value.findIndex((item) => {
      if (!item.isExpandable) return false

      if (item.isCourseBundles) {
        return sortedCourseBundlesOverview.value.some((bundle) => isActiveCourseBundle.value(bundle.courseBundleId))
      }

      if (item.isAcademyAdmin) {
        return item.subItems?.some((subItem) => route.path.startsWith(subItem.to || ''))
      }

      return false
    })

    expandedIndex.value = activeExpandableIndex !== -1 ? activeExpandableIndex : 1
  }

  const createCheckout = async (bundle: CourseBundleOverview) => {
    const course = bundle.courses[0]
    if (course?.id) {
      await handleWebsiteCheckout(course.id)
    }
  }

  onMounted(() => {
    setActiveExpandableIndex()
  })
</script>

<template lang="pug">
.sidebar(
  :class='{ "is-open": isOpen }',
  v-on-click-outside='closeSidebar',
  @keydown='handleKeydown',
  role='navigation',
  aria-label='Main navigation'
)
  .sidebar-header
    h3 {{ $t('overview') }}
    .close-icon(@click='closeSidebar', aria-label='Close sidebar')
      SVGRenderer(
        :icon='closeIcon',
        height='25px',
        width='25px',
        stroke-color='var(--primary-text-color)',
        :has-hover='false'
      )
  .sidebar-content
    nav
      template(v-for='(item, index) in navigationItems')
        router-link.sidebar-item(
          v-if='!item.isExpandable',
          :key='item.label',
          :to='item.to',
          @click.native='closeSidebarAndResetExpandedIndex',
          :aria-label='$t(item.label)'
        )
          .icon-item-wrapper
            SVGRenderer(
              v-if='item.icon',
              :icon='item.icon.value',
              height='25px',
              width='25px',
              stroke-color='var(--primary-text-color)',
              :has-hover='false'
            )
            h5 {{ $t(item.label) }}
          SVGRenderer(
            :icon='arrowRight',
            height='15px',
            width='15px',
            stroke-color='var(--primary-text-color)',
            :has-hover='false'
          )

        .expandable-item(v-else, :key='item.label', :class='{ "is-expanded": expandedIndex === index }')
          .sidebar-item(@click='toggleExpand(index)')
            .icon-item-wrapper
              SVGRenderer(
                v-if='item.icon',
                :icon='item.icon.value',
                height='25px',
                width='25px',
                stroke-color='var(--primary-text-color)',
                :has-hover='false'
              )
              h5 {{ $t(item.label) }}
            .arrow-icon
              SVGRenderer(
                :icon='arrowRight',
                height='15px',
                width='15px',
                stroke-color='var(--primary-text-color)',
                :has-hover='false'
              )

          .sub-items(v-if='expandedIndex === index')
            .course-bundles(v-if='expandedIndex === index && item.isCourseBundles')
              .sub-item(
                v-for='bundle in sortedCourseBundlesOverview',
                :key='bundle.courseBundleId',
                @click='setCourseBundleAndCourse(bundle.courseBundleId)'
              )
                .icon-item-wrapper
                  SVGRenderer(
                    :icon='courseVerticalIcon(bundle.title)',
                    width='45px',
                    height='45px',
                    :has-hover='false'
                  )
                  .item-content
                    h5(:class='{ "is-active": isActiveCourseBundle(bundle.courseBundleId) }') {{ bundle.sector }}
                    .status-text(:class='{ "is-column": hasTrialCourse(bundle) }', v-if='hasValidUser')
                      h6.status-item(v-for='(status, index) in bundleStatus(bundle)', :key='index')
                        template(v-if='status.isExpired')
                          span {{ status.text }}
                          a.checkout-link(@click.stop='createCheckout(bundle)') {{ status.action }}
                        template(v-else)
                          span {{ status.text }}
                SVGRenderer(
                  :icon='arrowRight',
                  height='12px',
                  width='12px',
                  stroke-color='var(--primary-text-color)',
                  :has-hover='false'
                )
            .admin-items(v-if='item.isAcademyAdmin')
              .sub-item(v-for='subItem in item.subItems', :key='subItem.label')
                router-link.h5(:to='subItem.to', @click.native='closeSidebar') {{ $t(subItem.label) }}
                SVGRenderer(
                  :icon='arrowRight',
                  height='12px',
                  width='12px',
                  stroke-color='var(--primary-text-color)',
                  :has-hover='false'
                )
      .sidebar-item(v-if='showReTriggerOnboardingPopup && isMobileDevice')
        h5(@click='triggerOnboardingPopup') {{ $t('intro') }}
        SVGRenderer(
          :icon='arrowRight',
          height='15px',
          width='15px',
          stroke-color='var(--primary-text-color)',
          :has-hover='false'
        )
      Search(v-if='isMobileDevice')
  .sidebar-footer(v-if='hasValidUser')
    router-link.logout(to='/logout', @click.native='closeSidebar', :aria-label='$t("logout")')
      h5.ketch-font-bold {{ $t('logout') }}
</template>

<style lang="postcss" scoped>
  .sidebar {
    @apply ketch-fixed ketch-top-0 ketch-left-0 ketch-bottom-0 ketch-h-screen ketch-w-[300px] ketch-z-[60];
    @apply ketch-bg-content-background-color ketch-shadow-lg ketch-transform ketch-transition-transform ketch-duration-300 ketch-ease-in-out;
    @apply ketch-flex ketch-flex-col ketch-w-full sm:ketch-w-[415px];

    transform: translateX(-100%);
    box-shadow: 4px 0 10px rgba(0, 0, 0, 0.1);

    &.is-open {
      transform: translateX(0);
    }

    .sidebar-header {
      @apply ketch-flex ketch-items-center ketch-justify-between ketch-px-c10 ketch-py-c20 sm:ketch-px-c20;

      .close-icon {
        @apply ketch-cursor-pointer;
      }
    }

    .sidebar-content {
      @apply ketch-flex-1 ketch-overflow-y-auto;

      nav {
        @apply ketch-flex ketch-flex-col;

        .icon-item-wrapper {
          @apply ketch-flex ketch-items-center ketch-space-x-c10;

          .item-content {
            @apply ketch-flex ketch-flex-col ketch-space-y-c5;
          }
        }

        .sidebar-item {
          @apply ketch-flex ketch-items-center ketch-justify-between ketch-p-c10 ketch-cursor-pointer sm:ketch-p-c20;

          &:not(:last-child) {
            @apply ketch-border-b ketch-border-border-color;
          }

          &.router-link-exact-active {
            h5 {
              @apply ketch-font-bold;
            }
          }
        }

        .expandable-item {
          @apply ketch-flex ketch-flex-col;

          .arrow-icon {
            transition: transform 0.3s ease;
          }

          &.is-expanded {
            background: linear-gradient(180deg, #f2f2f5 0%, #e8e9eb 100%);

            .sidebar-item {
              @apply ketch-border-b-0 ketch-pb-0;
            }

            .arrow-icon {
              transform: rotate(90deg);
            }
          }

          .sidebar-item {
            @apply ketch-flex ketch-items-center ketch-justify-between ketch-w-full ketch-p-c10 sm:ketch-p-c20;
            @apply ketch-border-b ketch-border-border-color ketch-cursor-pointer;
          }
        }

        .sub-items {
          .course-bundles,
          .admin-items {
            @apply ketch-flex ketch-flex-col ketch-space-y-c20 ketch-p-c10 sm:ketch-p-c20;
            @apply ketch-border-b ketch-border-border-color;

            .sub-item {
              @apply ketch-flex ketch-items-center ketch-justify-between ketch-cursor-pointer;
              @apply ketch-p-c5 ketch-rounded-[10px] ketch-bg-content-background-color sm:ketch-p-c10;

              h5.is-active {
                @apply ketch-font-bold ketch-text-primary-color;
              }

              .router-link-exact-active {
                @apply ketch-font-bold;
              }
            }
          }

          .course-bundles {
            background: linear-gradient(180deg, #f2f2f5 0%, #e8e9eb 100%);
          }
        }

        .search-wrapper {
          @apply ketch-p-c15 ketch-border-b ketch-border-border-color;
        }

        .status-text {
          @apply ketch-flex ketch-flex-row ketch-space-x-c5;

          &.is-column {
            @apply ketch-flex-col ketch-space-x-0;
          }

          .status-item {
            @apply ketch-font-bold;
          }

          .checkout-link {
            @apply ketch-underline ketch-cursor-pointer ketch-ml-c2;
          }
        }
      }
    }

    .sidebar-footer {
      @apply ketch-flex ketch-flex-col ketch-space-y-c20 ketch-p-c20 ketch-border-t;

      a.logout {
        @apply ketch-block ketch-w-full ketch-bg-primary-color ketch-text-primary-foreground-color;
        @apply ketch-rounded-normal ketch-text-center ketch-py-c10;
        @apply hover:ketch-bg-opacity-80 hover:ketch-transition-all hover:ketch-duration-200;
      }
    }
  }
</style>
